import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import {
  loadOrCreateLocalWallet,
  loadOrCreateRemoteWallet,
} from '../services/rewardWallet';

const RewardsContext = createContext(null);

export function RewardsProvider({ children }) {
  const { user, isReady: isAuthReady, isFirebaseConfigured } = useAuth();
  const [loadedWallet, setLoadedWallet] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  const source = isFirebaseConfigured && db ? 'firestore' : 'local';
  const wallet =
    loadedWallet?.uid === user?.id && loadedWallet?.source === source
      ? loadedWallet.value
      : null;

  const refresh = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!isAuthReady) {
      setIsLoading(true);
      return null;
    }

    const uid = user?.id;
    if (!uid) {
      setLoadedWallet(null);
      setError('');
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    setError('');

    try {
      const nextWallet = source === 'firestore'
        ? await loadOrCreateRemoteWallet(db, uid)
        : loadOrCreateLocalWallet(uid);

      if (requestIdRef.current === requestId) {
        setLoadedWallet({ uid, source, value: nextWallet });
      }
      return nextWallet;
    } catch (loadError) {
      console.warn('[Rewards] Unable to load the reward wallet.', loadError);
      if (requestIdRef.current === requestId) {
        setError('Unable to load your rewards. Please try again.');
      }
      return null;
    } finally {
      if (requestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [isAuthReady, source, user?.id]);

  useEffect(() => {
    refresh();
    return () => {
      requestIdRef.current += 1;
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      wallet,
      isLoading,
      error,
      refresh,
      source,
      isLocalFallback: source === 'local',
    }),
    [error, isLoading, refresh, source, wallet]
  );

  return <RewardsContext.Provider value={value}>{children}</RewardsContext.Provider>;
}

export function useRewards() {
  const context = useContext(RewardsContext);
  if (!context) {
    throw new Error('useRewards must be used inside RewardsProvider');
  }
  return context;
}
